<template>
  <GlobalLoader/>
  <div class="card shadow-sm border-0">
    <div class="bg-blue-light p-1">
      <TitleButton
          :showBtn="true"
          :showAddNew="true"
          :btnTitle="'Insert'"
          @onClickAddNewButton="insertIntoTable"
          @onClickCloseButton="onClickCloseButton"
          title="Upload Reward Point"
      />
    </div>

    <div class="p-3">
      <template v-for="(item, key) in csvData" :key="key">
        <div v-if="!uploadedBatches.includes(item.key)" :class="key != 0 ? 'mt-4' : ''">
          <div class="d-flex align-items-center gap-2 mb-2 pb-1 border-bottom">
            <span class="badge bg-primary rounded-pill px-3 py-1 fs-6">Batch {{ item.key }}</span>
            <div v-if="item.key === uploadingBatch" class="spinner-border spinner-border-sm text-success" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
          </div>
          <div class="px-1">
            <div v-for="(child, index) in item.data" :key="index" class="mb-2 rounded border" :class="child.status === 'error' ? 'border-danger' : 'border-light'">
              <div class="d-flex align-items-center justify-content-between px-3 py-2" :class="child.status === 'error' ? 'bg-danger bg-opacity-10' : 'bg-light'">
                <div class="d-flex align-items-center gap-2">
                  <i class="fas fa-user-circle text-secondary fs-5"></i>
                  <span class="fw-semibold" :class="child.status === 'error' ? 'text-danger' : 'text-dark'">
                    {{ getTitle(child.data) }}
                  </span>
                  <span v-if="child.status === 'error'" class="badge bg-danger bg-opacity-75 small ms-1">
                    {{ getErrors(child.validation) }}
                  </span>
                </div>
                <div class="d-flex align-items-center gap-2">
                  <button
                      class="btn btn-sm d-flex align-items-center gap-1"
                      :class="editingKey === `${item.key}-${index}` ? 'btn-secondary' : 'btn-primary'"
                      @click="toggleEdit(item.key, index, child)"
                  >
                    <i :class="editingKey === `${item.key}-${index}` ? 'fas fa-times' : 'fas fa-pencil-alt'"></i>
                    <span>{{ editingKey === `${item.key}-${index}` ? 'Close' : 'Edit' }}</span>
                  </button>
                  <button
                      v-if="child.status === 'error'"
                      class="btn btn-sm btn-danger d-flex align-items-center gap-1"
                      :disabled="saving"
                      @click="deleteRow(item, index, child)"
                  >
                    <i class="fas fa-trash-alt"></i>
                    <span>Delete</span>
                  </button>
                </div>
              </div>
              <div v-if="editingKey === `${item.key}-${index}`" class="px-3 py-2 bg-white">
                <div class="row g-1">
                  <div class="col-12 col-sm-6 col-md-3">
                    <label class="form-label mb-0 fw-bold" style="font-size:0.8rem;color:#212529">Customer</label>
                    <AsyncSelect
                        placeholder="Select Customer"
                        v-model="selectedCustomerId"
                        :api-service="fetchContactProfiles"
                        :additional-query="{roles: 'customer', status: 'active'}"
                        :additional-option="editCustomerOption"
                        label="name"
                        :reduce="name => name.id"
                    />
                  </div>
                  <div class="col-12 col-sm-6 col-md-3">
                    <label class="form-label mb-0 fw-bold" style="font-size:0.8rem;color:#212529">Points</label>
                    <input v-model="editForm.points" type="number" class="form-control form-control-sm" placeholder="Points">
                  </div>
                  <div class="col-12 col-sm-6 col-md-3">
                    <label class="form-label mb-0 fw-bold" style="font-size:0.8rem;color:#212529">Note</label>
                    <input v-model="editForm.description" type="text" class="form-control form-control-sm" placeholder="Note">
                  </div>
                  <div class="col-12 col-sm-6 col-md-3">
                    <label class="form-label mb-0 fw-bold" style="font-size:0.8rem;color:#212529">Adjustment Date</label>
                    <input :value="editForm.date" type="text" class="form-control form-control-sm" readonly>
                  </div>
                </div>
                <div class="mt-2 d-flex gap-2">
                  <button class="btn btn-sm btn-primary px-3" :disabled="saving" @click="saveRow(item, index)">
                    <i class="fas fa-save me-1"></i>Save
                  </button>
                  <button class="btn btn-sm btn-outline-secondary px-3" @click="cancelEdit">
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import {ref, onMounted, inject} from 'vue'
import {useRoute, useRouter} from 'vue-router'
import {useStore} from 'vuex'
import TitleButton from '@/components/atom/TitleButton.vue'
import GlobalLoader from '@/components/atom/GlobalLoader.vue'
import AsyncSelect from '@/components/molecule/input-field/AsyncSelect.vue'
import handleInventory from '@/services/modules/inventory'
import handleContact from '@/services/modules/contact'

const route = useRoute()
const router = useRouter()
const store = useStore()
const showError = inject('showError')
const showSuccess = inject('showSuccess')

const {fetchRewardPointRows, uploadRewardPointFinalData, updateRewardPointRow, deleteRewardPointRow} = handleInventory()
const {fetchContactProfiles} = handleContact()

const csvData = ref([])
const uploadingBatch = ref('')
const uploadedBatches = ref([])
const editingKey = ref(null)
const editForm = ref({})
const selectedCustomerId = ref(null)
const editCustomerOption = ref([])
const saving = ref(false)

const getQuery = () => {
  return `?id=${route.query.rowId}&company_id=${route.params.companyId}`
}

const getTitle = (data) => {
  let title = data.serial_no ? data.serial_no : 'N/A'
  if (data.name) title += ` - ${data.name}`
  title += ` (${data.points} points)`
  return title
}

const getErrors = (validation) => {
  if (!validation) return ''
  return Object.values(validation).flat().join(', ')
}

const toggleEdit = (batchKey, index, child) => {
  const key = `${batchKey}-${index}`
  if (editingKey.value === key) {
    cancelEdit()
    return
  }
  editingKey.value = key
  editForm.value = {...child.data}
  selectedCustomerId.value = child.data.contact_profile_id || null
  editCustomerOption.value = setEditCustomerOption(child.data)
}

const setEditCustomerOption = (data) => {
  if (!data.contact_profile_id) return []
  return [{id: data.contact_profile_id, name: data.name}]
}

const cancelEdit = () => {
  editingKey.value = null
  editForm.value = {}
  selectedCustomerId.value = null
  editCustomerOption.value = []
}

const saveRow = (item, index) => {
  saving.value = true
  const formCopy = {...editForm.value, contact_profile_id: selectedCustomerId.value}

  const payload = {
    company_id: route.params.companyId,
    id: route.query.rowId,
    row_id: formCopy.id,
    data: formCopy,
  }

  updateRewardPointRow(payload).then((res) => {
    if (!res.status) return showError(res.message)
    item.data[index] = res.data
    showSuccess(res.message)
    cancelEdit()
  }).finally(() => {
    saving.value = false
  })
}

const deleteRow = (item, index, child) => {
  if (saving.value) return
  saving.value = true

  const payload = {
    company_id: route.params.companyId,
    id: route.query.rowId,
    row_id: child.data.id,
  }

  deleteRewardPointRow(payload).then((res) => {
    if (!res.status) return showError(res.message)
    item.data.splice(index, 1)
    showSuccess(res.message)
  }).finally(() => {
    saving.value = false
  })
}

const onClickCloseButton = () => {
  router.push({name: 'reward-point-import', params: route.params, query: route.query})
}

const insertIntoTable = () => {
  if (store.state.loading) return
  store.state.loading = true
  uploadBatch(0)
}

const uploadBatch = (index) => {
  if (index === csvData.value.length) {
    store.state.loading = false
    router.back()
    return
  }

  const formData = {
    company_id: route.params.companyId,
    id: route.query.rowId,
    batch: csvData.value[index].key,
  }

  uploadingBatch.value = csvData.value[index].key

  uploadRewardPointFinalData(formData).then((res) => {
    if (!res.status) {
      store.state.loading = false
      return showError(res.message)
    }
    uploadedBatches.value.push(uploadingBatch.value)
    uploadingBatch.value = ''
    setTimeout(() => {
      uploadBatch(index + 1)
    }, 1500)
  })
}

onMounted(() => {
  fetchRewardPointRows(getQuery()).then((res) => {
    if (!res.status) return
    csvData.value = Object.keys(res.data).map((key) => ({
      key,
      data: res.data[key],
    }))
  })
})
</script>
